import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Flex } from '../../../components/Flex';
import { Section } from '../../../components/Section';
import { Text } from '../../../components/Text';
import { atiraLinkTestIDs } from '../../../constants/test/atiralink.testIds';
import { CreateAtiraLinkDto } from '../../../model/atira-link/dto/CreateAtiraLinkDto';
import { Lengths } from '../../../model/shared/enum/Lengths.enum';
import { LinkInput } from './LinkInput';

const fieldToValueInputPlaceholder: Record<string, string> = {
  phone: '+0123456789',
  email: 'example@example.com',
  website: 'https://www.example.com',
  map: 'https://www.google.com/maps',
};

type BasicField = 'phone' | 'email' | 'website' | 'map';

export const AtiraLinkContentBasic: React.FC = () => {
  const { t } = useTranslation();

  const {
    control,
    formState: { errors },
    clearErrors,
  } = useFormContext<CreateAtiraLinkDto>();

  const getInputMaxLength = (field: BasicField) => {
    switch (field) {
      case 'phone':
        return Lengths.PHONE;
      case 'email':
        return Lengths.EMAIL;
      case 'website':
      case 'map':
      default:
        return Lengths.DESCRIPTION;
    }
  };

  const getInputType = (field: BasicField) => {
    switch (field) {
      case 'phone':
        return 'tel';
      case 'email':
        return 'email';
      case 'website':
      case 'map':
        return 'url';
      default:
        return 'text';
    }
  };

  return (['phone', 'email', 'website', 'map'] as const).map((field) => (
    <Section title={t(`common.${field}`)}>
      <Flex flexDirection="column" key={field} gap="s">
        <Controller
          control={control}
          name={`${field}.label` as keyof CreateAtiraLinkDto}
          render={({ field: { value, onChange } }) => (
            <LinkInput
              title={t(`atira_link.create.basic.${field}.label`)}
              value={value as string}
              onChange={onChange}
              type="text"
              placeholder={t(`atiraLink.create.content.${field}.example`)}
              maxLength={Lengths.NAME}
              dataTestId={atiraLinkTestIDs.content[field].label}
              valid={!errors[field]}
              onFocus={() => clearErrors(field)}
            />
          )}
        />

        <Controller
          control={control}
          name={`${field}.value` as keyof CreateAtiraLinkDto}
          render={({ field: { value, onChange } }) => (
            <LinkInput
              value={value as string}
              title={t(`atira_link.create.basic.${field}.value`)}
              onChange={(e) => {
                if (field === 'phone') {
                  const numericValue = e.currentTarget.value
                    .replace(/(?!^\+)[^0-9]/g, '')
                    .trim();
                  onChange(numericValue);
                } else {
                  onChange(e.currentTarget.value);
                }
              }}
              placeholder={
                fieldToValueInputPlaceholder[
                  field as keyof typeof fieldToValueInputPlaceholder
                ]
              }
              maxLength={getInputMaxLength(field)}
              dataTestId={atiraLinkTestIDs.content[field].value}
              valid={!errors[field]}
              onFocus={() => clearErrors(field)}
              type={getInputType(field)}
            />
          )}
        />

        {errors[field] ? (
          <Text color="danger" fontSize="s">
            {errors[field]?.message}
          </Text>
        ) : null}
      </Flex>
    </Section>
  ));
};
